const CLIENT_COOKIE = '_ga';
const SESSION_COOKIE_PREFIX = '_ga_';
const CONSENT_COOKIE = 'cookieyes-consent';
const ANALYTICS_CATEGORY = 'analytics';
const GRANTED = 'granted';
const DENIED = 'denied';

export const ANALYTICS_HEADER = 'X-DP-Analytics';

/**
 * Landmine: calling decodeURIComponent directly. A malformed cookie throws URIError, and this
 * runs as an axios request interceptor, so the throw rejects the chain and the mutation never
 * leaves the browser - analytics breaking the request it was only supposed to describe.
 */
function decode(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

function cookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));

  return match ? decode(match[1]) : null;
}

/**
 * CookieYes writes comma-separated `key:value` pairs. Parsing them rather than substring
 * matching on `analytics:yes` means a future category such as `subanalytics:yes` cannot be
 * mistaken for a grant.
 */
export function consentState(raw = cookie(CONSENT_COOKIE)) {
  if (!raw) {
    return DENIED;
  }

  const analytics = raw
    .split(',')
    .map((pair) => pair.split(':'))
    .find(([key]) => key && key.trim() === ANALYTICS_CATEGORY);

  return analytics && analytics[1] && analytics[1].trim() === 'yes' ? GRANTED : DENIED;
}

/**
 * The last two dot-separated segments, matching AnalyticsIdentity::clientId() on the server.
 */
export function clientId(raw = cookie(CLIENT_COOKIE)) {
  if (!raw) {
    return null;
  }

  const segments = raw.split('.');

  if (segments.length < 4) {
    return null;
  }

  const [identifier, timestamp] = segments.slice(-2);

  return /^\d+$/.test(identifier) && /^\d+$/.test(timestamp) ? `${identifier}.${timestamp}` : null;
}

export function sessionId(raw = discoverSessionCookie()) {
  if (!raw) {
    return null;
  }

  const payload = raw.split('.')[2];

  if (!payload) {
    return null;
  }

  if (/^\d+$/.test(payload)) {
    return payload;
  }

  const session = payload.split('$').find((field) => field.startsWith('s') && /^\d+$/.test(field.slice(1)));

  return session ? session.slice(1) : null;
}

/**
 * Discovering the cookie by prefix avoids threading the GA4 stream id through thirteen host
 * pages. Two `_ga_*` cookies means two properties and no way to tell which is ours, so the
 * session id is skipped rather than guessed - it is optional, the client id is not.
 */
function discoverSessionCookie() {
  const matches = document.cookie
    .split(';')
    .map((entry) => entry.trim())
    .filter((entry) => entry.startsWith(SESSION_COOKIE_PREFIX));

  return matches.length === 1 ? decode(matches[0].split('=').slice(1).join('=')) : null;
}

/**
 * Returns null when there is nothing usable to send, so the caller sets no header at all and
 * the server drops the event. Never mint an id: a plausible-but-wrong one corrupts a session
 * GA4 already holds.
 */
export function buildAnalyticsHeader() {
  const consent = consentState();

  if (consent !== GRANTED) {
    return null;
  }

  const cid = clientId();

  if (!cid) {
    return null;
  }

  const sid = sessionId();

  return [`cid=${cid}`, sid ? `sid=${sid}` : null, `consent=${consent}`].filter(Boolean).join(';');
}

/**
 * Register on every GraphQL axios client, and only those - the REST clients target an API the
 * mutation resolver never sees. Nothing enforces the "every": a client without this sends no
 * identity, so its mutations are dropped before an event exists and nothing reports the loss.
 */
export function analyticsHeaderInterceptor(config) {
  try {
    const header = buildAnalyticsHeader();

    if (header) {
      config.headers[ANALYTICS_HEADER] = header;
    }
  } catch {
    return config;
  }

  return config;
}
